'use client'
import srOnly from '@fukku/styles/src/srOnly.module.css'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { sendChangeColorEvent } from 'product/analytics/sendChangeColorEvent'
import type { ProductColor } from 'product/types'
import { GenericEvents as ProductGenericEvents } from 'product/types/GenericEvents'
import {
	ColorOption,
	type OnClickColorOptionProps,
} from 'product-card/components/shared/ProductColor/ColorOption/ColorOption'
import { dataTestIds } from 'product-card/constants/dataTestIds'
import { useProductCard } from 'product-card/hooks/useProductCard'
import { reorderColorsById } from 'product-card/utils/reorderColorsById'
import { useCallback, useId, useMemo } from 'react'
import { cx } from 'utils/cx'

import styles from './ColorPicker.module.scss'

interface ColorPickerProps {
	readonly productColors: ProductColor[]
	readonly showAsFeatured?: boolean
	readonly className?: string
	readonly onChangeColor?: () => void
}

export const ColorPicker = ({
	productColors,
	showAsFeatured = false,
	className,
	onChangeColor,
}: ColorPickerProps) => {
	const { t } = useLabels()

	const {
		defaultColorId,
		selectedColorId,
		setSelectedColorId,
		productId,
		setProductId,
		location,
		initialProductId,
	} = useProductCard()

	const colorPickerId = useId()

	const sortedColors = useMemo(
		() =>
			reorderColorsById({
				productColors,
				defaultColor: defaultColorId,
			}),
		[defaultColorId, productColors]
	)

	const shouldRenderColorPicker = !showAsFeatured && sortedColors.length > 1

	const handleClick = useCallback(
		({ colorId, relatedProductId }: OnClickColorOptionProps) => {
			setSelectedColorId(colorId)
			sendChangeColorEvent({
				eventName: ProductGenericEvents.PLP_ACTION,
				productId,
				colorId,
				location,
			})
			// If no related product is provided for this color, we restore the reference with which the card was initially loaded
			setProductId?.(relatedProductId ?? initialProductId)

			onChangeColor?.()
		},
		[
			initialProductId,
			location,
			onChangeColor,
			productId,
			setProductId,
			setSelectedColorId,
		]
	)

	if (!shouldRenderColorPicker) {
		return null
	}

	return (
		<fieldset
			data-testid={dataTestIds.productCardColorPicker}
			className={cx(styles.colorsList, className)}
		>
			<legend className={srOnly.className}>
				{t('accessibility.colorSelector.text')}
			</legend>
			{sortedColors.map((color) => (
				<ColorOption
					key={`${colorPickerId}-${color.id}`}
					name={`color-picker-${colorPickerId}`}
					dataTestId={`${dataTestIds.productCardColorPicker}.button.${color.id}`}
					color={color}
					isSelected={color.id === selectedColorId}
					onClickColor={handleClick}
				/>
			))}
		</fieldset>
	)
}
